// Code generated by master-scheme ServiceTypesGenerator - DO NOT EDIT
// Generator: /Users/victorvins/Projects/master-scheme/dsl/src/ts-api/ServiceTypesGenerator.scala

import { postEndpoint } from '@innolab-trader/tradovate-transport'
import type { ValidationError } from '@innolab-trader/tradovate-types'
import type { MFAOptions } from '@innolab-trader/tradovate-types'
import type { HibpHint } from '@innolab-trader/tradovate-types/src/enums/hibpHint'
import type { SocialSignInProvider } from '@innolab-trader/tradovate-types/src/enums/socialSignInProvider'
import type { UserExperience } from '@innolab-trader/tradovate-types/src/enums/userExperience'
import type { UserStatus } from '@innolab-trader/tradovate-types/src/enums/userStatus'

export interface SocialAccessTokenRequest {
  provider: SocialSignInProvider
  token: string
  appId?: string
  appVersion?: string
  deviceId?: string
  userAgent?: string
  organization?: string
  locale?: string
  coapplicantCode?: string
}

export interface AccessTokenResponse {
  errorText?: string
  hibpHint?: HibpHint
  accessToken?: string
  mdAccessToken?: string
  expirationTime?: string
  passwordExpirationTime?: string
  userStatus?: UserStatus
  userId?: number
  name?: string
  hasLive?: boolean
  hasSimPlus?: boolean
  outdatedTaC?: boolean
  hasFunded?: boolean
  hasMarketData?: boolean
  requiredNonProCertification?: boolean
  newTrialEligible?: boolean
  outdatedLiquidationPolicy?: boolean
  outdatedSentimentPolicy?: boolean
  mfa?: MFAOptions
  experience?: UserExperience
  orgName?: string
}

/**
 * Validate SocialAccessTokenRequest request
 *
 * Follows Rust validation pattern with structured errors
 *
 * @param request - The request object to validate
 * @returns Array of validation errors, empty if valid
 *
 * @example
 * ```typescript
 * const errors = validateSocialAccessTokenRequest(requestData)
 * if (errors.length > 0) {
 *   return { status: 400, errors }
 * }
 * ```
 */
export function validateSocialAccessTokenRequest(request: SocialAccessTokenRequest): ValidationError[] {
  const errors: ValidationError[] = []

  const req = request as unknown as Record<string, unknown>
  if (req.provider === undefined || req.provider === null) {
    errors.push({
      field: 'provider',
      code: 'required',
      message: 'provider is required'
    })
  }

  if (req.token === undefined || req.token === null) {
    errors.push({
      field: 'token',
      code: 'required',
      message: 'token is required'
    })
  }
  if (typeof req.token === 'string' && req.token.length > 8192) {
    errors.push({
      field: 'token',
      code: 'too_long',
      message: 'token must not exceed 8192 characters',
      argument: '8192'
    })
  }
  if (typeof req.token === 'string' && req.token.trim() === '') {
    errors.push({
      field: 'token',
      code: 'required',
      message: 'token cannot be empty'
    })
  }

  if (typeof req.appId === 'string' && req.appId.length > 64) {
    errors.push({
      field: 'appId',
      code: 'too_long',
      message: 'appId must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.appVersion === 'string' && req.appVersion.length > 64) {
    errors.push({
      field: 'appVersion',
      code: 'too_long',
      message: 'appVersion must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.deviceId === 'string' && req.deviceId.length > 64) {
    errors.push({
      field: 'deviceId',
      code: 'too_long',
      message: 'deviceId must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.userAgent === 'string' && req.userAgent.length > 512) {
    errors.push({
      field: 'userAgent',
      code: 'too_long',
      message: 'userAgent must not exceed 512 characters',
      argument: '512'
    })
  }

  if (typeof req.organization === 'string' && req.organization.length > 64) {
    errors.push({
      field: 'organization',
      code: 'too_long',
      message: 'organization must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.locale === 'string' && req.locale.length > 64) {
    errors.push({
      field: 'locale',
      code: 'too_long',
      message: 'locale must not exceed 64 characters',
      argument: '64'
    })
  }

  if (typeof req.coapplicantCode === 'string' && req.coapplicantCode.length > 8192) {
    errors.push({
      field: 'coapplicantCode',
      code: 'too_long',
      message: 'coapplicantCode must not exceed 8192 characters',
      argument: '8192'
    })
  }

  return errors
}

export const socialAccessTokenRequest = postEndpoint<SocialAccessTokenRequest, AccessTokenResponse>('/auth/socialAccessTokenRequest')
